// Safe wrappers around Web Storage.
//
// In cross-origin / sandboxed iframes (e.g. the rocket.new preview), some
// browsers block third-party storage and even *accessing* `window.localStorage`
// or `window.sessionStorage` throws a SecurityError. An unguarded access during
// initial render (e.g. reading a saved language) crashes the whole React tree,
// which shows up as a blank preview / "navigation not working".
//
// These helpers never throw: they fall back to an in-memory store so the app
// keeps working even when persistent storage is unavailable.

const memoryStore = new Map();

const buildSafeStorage = (getNativeStorage) => ({
  getItem(key) {
    try {
      return getNativeStorage()?.getItem(key);
    } catch {
      return memoryStore.has(key) ? memoryStore.get(key) : null;
    }
  },
  setItem(key, value) {
    const str = String(value);
    try {
      getNativeStorage()?.setItem(key, str);
    } catch {
      memoryStore.set(key, str);
    }
  },
  removeItem(key) {
    try {
      getNativeStorage()?.removeItem(key);
    } catch {
      memoryStore.delete(key);
    }
  },
});

export const safeLocalStorage = buildSafeStorage(() => window.localStorage);
export const safeSessionStorage = buildSafeStorage(() => window.sessionStorage);

export default safeLocalStorage;
